import {
  faqKnowledgeIdFormat,
  merchantKnowledgeIdFormat,
  suggestedMerchantKnowledgeIdFormat,
} from "@redotech/shopper-agent-knowledge-id";
import * as z from "zod";

const DateishSchema = z.preprocess((value) => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date;
  }
  return value;
}, z.date());

const NullableDateishSchema = DateishSchema.nullish().transform(
  (value) => value ?? null,
);

const EscalatedSupportTicketSchema = z.object({
  id: z.string(),
  status: z.string(),
});

const ConciergeConversationCustomerSchema = z
  .object({
    id: z.string(),
    displayName: z.string().nullish().transform(nullIfMissing),
    firstName: z.string().nullish().transform(nullIfMissing),
    lastName: z.string().nullish().transform(nullIfMissing),
    email: z.string().nullish().transform(nullIfMissing),
    phone: z.string().nullish().transform(nullIfMissing),
  })
  .nullish()
  .transform((value) => value ?? null);

export const ConciergeConversationSummarySchema = z.object({
  topicId: z.string(),
  channelThreadId: z.string(),
  channel: z.string(),
  mongoCustomerId: z.string(),
  customer: ConciergeConversationCustomerSchema,
  numCustomerMessages: z.number(),
  numMerchantMessages: z.number(),
  lastCustomerMessageAt: NullableDateishSchema,
  lastMerchantMessageAt: NullableDateishSchema,
  lastCustomerMessageContent: z.string().nullish().transform(nullIfMissing),
  escalatedSupportTicket: EscalatedSupportTicketSchema.nullish().transform(
    (value) => value ?? null,
  ),
  createdAt: DateishSchema,
  updatedAt: DateishSchema,
});
export type ConciergeConversationSummary = z.infer<
  typeof ConciergeConversationSummarySchema
>;

export const ConciergeConversationListOutputSchema = z.object({
  conversations: z.array(ConciergeConversationSummarySchema),
});
export type ConciergeConversationListOutput = z.infer<
  typeof ConciergeConversationListOutputSchema
>;

export const ConciergeMessageSchema = z.object({
  id: z.string(),
  channelThreadId: z.string(),
  content: z.string().nullish().transform(nullIfMissing),
  channel: z.string(),
  senderType: z.string(),
  sentAt: DateishSchema,
});
export type ConciergeMessage = z.infer<typeof ConciergeMessageSchema>;

export const ConciergeConversationDetailOutputSchema = z.object({
  conversation: ConciergeConversationSummarySchema.extend({
    messages: z.array(ConciergeMessageSchema),
  }),
});
export type ConciergeConversationDetailOutput = z.infer<
  typeof ConciergeConversationDetailOutputSchema
>;

const EmailSchema = z.object({ email: z.string().optional() }).passthrough();
const PhoneNumberSchema = z
  .object({ phoneNumber: z.string().optional(), phone: z.string().optional() })
  .passthrough();

const CustomerShapeSchema = z
  .object({
    _id: z.unknown().optional(),
    id: z.unknown().optional(),
    name: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    emails: z.array(EmailSchema).nullish(),
    phoneNumbers: z.array(PhoneNumberSchema).nullish(),
    tags: z.array(z.string()).nullish(),
    marketingSubscriptionStatuses: z
      .object({ email: z.string().optional(), sms: z.string().optional() })
      .nullish(),
    supportCommunicationConsent: z.unknown().optional(),
    origin: z.string().nullish(),
  })
  .passthrough();

export const ConciergeCustomerOutputSchema = z.object({
  customer: CustomerShapeSchema,
});
export type ConciergeCustomerOutput = z.infer<
  typeof ConciergeCustomerOutputSchema
>;

export const ConciergeKnowledgeSchema = z
  .object({
    /** The tool searches the whole agent-knowledge library, so an id is an
     * article (`mkn_`), a linked FAQ entry (`fkn_`), or an unreviewed proposal
     * (`smk_`). The knowledge page resolves all three. */
    id: z.union([
      merchantKnowledgeIdFormat.schema,
      faqKnowledgeIdFormat.schema,
      suggestedMerchantKnowledgeIdFormat.schema,
    ]),
    title: z.string(),
    knowledge: z.string(),
    whenToUse: z.string().nullish(),
    enabled: z.boolean(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    expiresAt: z.string().nullable(),
    isSuggestedKnowledge: z.boolean().optional(),
    recommendedProductIds: z.array(z.string()).nullish(),
    numberOfUses: z.number().nullish(),
  })
  .passthrough();
export type ConciergeKnowledge = z.infer<typeof ConciergeKnowledgeSchema>;

export const ConciergeKnowledgeSearchOutputSchema = z.array(
  ConciergeKnowledgeSchema,
);
export type ConciergeKnowledgeSearchOutput = z.infer<
  typeof ConciergeKnowledgeSearchOutputSchema
>;

export const ConciergeConfigRenderViewSchema = z.enum([
  "status",
  "channels",
  "trigger",
  "triggerCatalog",
]);
export type ConciergeConfigRenderView = z.infer<
  typeof ConciergeConfigRenderViewSchema
>;

const ConciergeConfigSettingsSchema = z
  .object({
    enabled: z.boolean().optional(),
    personaConfig: z.object({ name: z.string().optional() }).nullish(),
    extendedOutreachConfig: z
      .object({ enabled: z.boolean().optional() })
      .passthrough()
      .nullish(),
    replyConfig: z
      .object({ enabled: z.boolean().optional() })
      .passthrough()
      .nullish(),
    iMessageConfig: z
      .object({ enabled: z.boolean().optional() })
      .passthrough()
      .nullish(),
    channels: z.record(z.string(), z.unknown()).nullish(),
  })
  .passthrough();

const ConciergeChannelDefinitionSchema = z
  .object({
    channel: z.string(),
    name: z.string(),
    defaultEnabled: z.boolean(),
  })
  .passthrough();

const ConciergeTriggerSummarySchema = z
  .object({
    id: z.string(),
    supported: z.boolean(),
    definition: z
      .object({
        name: z.string(),
        category: z.string(),
        defaultEnabled: z.boolean(),
      })
      .passthrough()
      .optional(),
    effective: z
      .object({
        willConsiderSignal: z.boolean(),
        agentEnabled: z.boolean(),
        triggerEnabled: z.boolean(),
      })
      .passthrough()
      .optional(),
    unsupportedReason: z.string().optional(),
  })
  .passthrough();

export const ConciergeConfigOutputSchema = z
  .object({
    renderView: z.array(ConciergeConfigRenderViewSchema).min(1),
    settings: ConciergeConfigSettingsSchema.optional(),
    channelDefinitions: z.array(ConciergeChannelDefinitionSchema).optional(),
    trigger: ConciergeTriggerSummarySchema.optional(),
    triggerCatalog: z
      .object({
        triggers: z.array(ConciergeTriggerSummarySchema).optional(),
        unsupportedTriggers: z.array(ConciergeTriggerSummarySchema).optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type ConciergeConfigOutput = z.infer<typeof ConciergeConfigOutputSchema>;

const ConciergeConfigChangeSchema = z.object({
  label: z.string(),
  before: z.string(),
  after: z.string(),
});

export const ConciergeConfigUpdateOutputSchema = z
  .object({
    changed: z.array(z.string()).optional(),
    changes: z.array(ConciergeConfigChangeSchema).min(1),
  })
  .passthrough();
export type ConciergeConfigUpdateOutput = z.infer<
  typeof ConciergeConfigUpdateOutputSchema
>;

const ConciergeAutomationStepSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    title: z.string().optional(),
    nextStepIds: z.array(z.string()).optional(),
  })
  .passthrough();

const ConciergeAutomationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    enabled: z.boolean(),
    category: z.string().optional(),
    schemaType: z.string().optional(),
    stepCount: z.number().int().optional(),
    stepTypes: z.array(z.string()).optional(),
    steps: z.array(ConciergeAutomationStepSchema).optional(),
  })
  .passthrough();

const ConciergeAutomationContextSchema = z
  .object({
    triggerId: z.string(),
    triggerName: z.string(),
    schemaType: z.string().optional(),
  })
  .passthrough();

const ConciergeAutomationSummarySchema = z
  .object({
    automation: ConciergeAutomationSchema,
    concierge: ConciergeAutomationContextSchema,
  })
  .passthrough();

export const ConciergeAutomationListOutputSchema = z
  .object({
    automations: z.array(ConciergeAutomationSummarySchema),
    totalCount: z.number().int().optional(),
    failedToParseAutomations: z.array(z.unknown()).optional(),
  })
  .passthrough();
export type ConciergeAutomationListOutput = z.infer<
  typeof ConciergeAutomationListOutputSchema
>;

export const ConciergeAutomationDetailOutputSchema =
  ConciergeAutomationSummarySchema;
export type ConciergeAutomationDetailOutput = z.infer<
  typeof ConciergeAutomationDetailOutputSchema
>;

export function tryParseConciergeConversationList(
  output: unknown,
): ConciergeConversationListOutput | null {
  const result = ConciergeConversationListOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeConversationDetail(
  output: unknown,
): ConciergeConversationDetailOutput | null {
  const result = ConciergeConversationDetailOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeCustomer(
  output: unknown,
): ConciergeCustomerOutput | null {
  const result = ConciergeCustomerOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeKnowledge(
  output: unknown,
): ConciergeKnowledge | null {
  const result = ConciergeKnowledgeSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeKnowledgeSearch(
  output: unknown,
): ConciergeKnowledgeSearchOutput | null {
  const result = ConciergeKnowledgeSearchOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeConfig(
  output: unknown,
): ConciergeConfigOutput | null {
  const result = ConciergeConfigOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeConfigUpdate(
  output: unknown,
): ConciergeConfigUpdateOutput | null {
  const result = ConciergeConfigUpdateOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeAutomationList(
  output: unknown,
): ConciergeAutomationListOutput | null {
  const result = ConciergeAutomationListOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function tryParseConciergeAutomationDetail(
  output: unknown,
): ConciergeAutomationDetailOutput | null {
  const result = ConciergeAutomationDetailOutputSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function customerDisplayName(
  customer: ConciergeCustomerOutput["customer"],
): string {
  const explicit = customer.name?.trim();
  if (explicit) {
    return explicit;
  }
  const first = customer.firstName?.trim() ?? "";
  const last = customer.lastName?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  return full || "Customer";
}

export function primaryCustomerEmail(
  customer: ConciergeCustomerOutput["customer"],
): string | null {
  return (
    customer.emails?.find((entry) => entry.email?.trim())?.email?.trim() ?? null
  );
}

export function primaryCustomerPhone(
  customer: ConciergeCustomerOutput["customer"],
): string | null {
  const primary = customer.phoneNumbers?.find(
    (entry) => entry.phoneNumber?.trim() || entry.phone?.trim(),
  );
  return primary?.phoneNumber?.trim() ?? primary?.phone?.trim() ?? null;
}

export function customerId(
  customer: ConciergeCustomerOutput["customer"],
): string | null {
  return stringifyId(customer._id) ?? stringifyId(customer.id);
}

export function conversationState(
  conversation: ConciergeConversationSummary,
): "Escalated" | "Replied" | "Awaiting" {
  if (conversation.escalatedSupportTicket) {
    return "Escalated";
  }
  return conversation.numCustomerMessages > 0 ? "Replied" : "Awaiting";
}

function stringifyId(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  if (
    value &&
    typeof value === "object" &&
    "toString" in value &&
    typeof value.toString === "function"
  ) {
    const text = value.toString();
    return text === "[object Object]" ? null : text;
  }
  return null;
}

function nullIfMissing(value: string | null | undefined): string | null {
  return value ?? null;
}
